import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { API_URL } from '../../lib/utils';
import { 
  ArrowRight, 
  Search, 
  MapPin, 
  Briefcase,
  CheckCircle2,
  Building2,
  GraduationCap,
  Heart,
  Users,
  FileText,
  Shield,
  HelpCircle,
  Stethoscope,
  Building
} from 'lucide-react';
import { toast } from 'sonner';

const StateSocialWorkerJobsPage = ({
  stateName,
  stateAbbr,
  metaTitle,
  metaDescription,
  cities,
  stateDescription,
  faqItems = []
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStateJobs();
    
    // Set SEO meta tags
    document.title = metaTitle || `${stateName} Social Worker Jobs (LMSW, LCSW, MSW) | INeedASocialWorker`;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaDescription || `Find ${stateName} social worker jobs in hospitals, hospice, schools, behavioral health, and community agencies. Search by city, license (LMSW/LCSW/MSW), and apply fast.`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateName, metaTitle, metaDescription]);

  const fetchStateJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/jobs?location=${encodeURIComponent(stateName)}&limit=12`);
      setJobs(response.data.jobs);
      setTotalJobs(response.data.total);
    } catch (error) {
      console.error(`Error fetching ${stateName} jobs:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrioritySignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/priority-signup`, { email, state: stateName });
      toast.success('You\'re on the priority list! We\'ll notify you when jobs are posted.');
      setEmail('');
    } catch (error) {
      toast.success('You\'re on the priority list! We\'ll notify you when jobs are posted.');
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  const specialtyCategories = [
    { 
      title: "Hospital Social Worker Jobs", 
      description: "Acute care, discharge planning, ER, ICU, and inpatient medical settings.",
      href: "/hospital-social-worker-jobs",
      icon: Building2
    },
    { 
      title: "Hospice Social Worker Positions", 
      description: "Palliative care, home health, travel hospice, and PRN roles.",
      href: "/hospice-social-worker-jobs",
      icon: Heart
    },
    { 
      title: "School Social Worker Openings", 
      description: "Public schools, charter districts, and student support services.",
      href: "/school-social-worker-jobs",
      icon: GraduationCap
    },
    { 
      title: "Behavioral Health Roles", 
      description: "Inpatient psych, outpatient therapy, crisis stabilization, and substance use programs.",
      href: "/clinical-social-worker-jobs",
      icon: Users
    },
    { 
      title: "Medical Social Work Careers", 
      description: "Dialysis centers, skilled nursing facilities, rehab, and long-term care.",
      href: "/medical-social-worker-jobs",
      icon: Stethoscope
    },
    { 
      title: "Community & Government Positions", 
      description: "State agencies, CPS, VA, nonprofits, and county services.",
      href: "/community-social-worker-jobs",
      icon: Building
    },
  ];

  const defaultFaqItems = [
    {
      question: `What types of social work jobs are hiring in ${stateName} right now?`,
      answer: `${stateName} employers commonly hire for hospital/medical social work, hospice, school-based services, behavioral health, and community case management roles.`
    },
    {
      question: `Do I need an LCSW for most ${stateName} jobs?`,
      answer: "Not always. Many roles hire LMSW or MSW candidates, especially in case management, hospice, hospitals, and community programs. Some clinical roles prefer or require LCSW."
    },
    {
      question: "How do I apply faster?",
      answer: "Create your profile, upload your resume, and use one-click apply when available through our SW Express Apply™ feature."
    },
  ];

  const finalFaqItems = faqItems.length > 0 ? faqItems : defaultFaqItems;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="bg-secondary/20 text-secondary border-secondary/40 mb-4">
              <MapPin className="w-3 h-3 mr-1" /> {stateAbbr}
            </Badge>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {stateName} Social Worker Jobs
            </h1>
            <p className="text-lg text-gray-200 max-w-3xl mx-auto mb-8">
              Search LMSW, LCSW, and MSW jobs across {stateName} — hospitals, hospice, school districts, behavioral health, and community agencies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/jobs?location=${encodeURIComponent(stateName)}`}>
                <Button className="bg-secondary hover:bg-secondary-hover text-white px-8 h-12" data-testid={`search-${stateAbbr.toLowerCase()}-jobs`}>
                  <Search className="w-4 h-4 mr-2" />
                  Search {stateName} Jobs
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 h-12">
                  <FileText className="w-4 h-4 mr-2" />
                  Upload Resume (AI Parse)
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Search Intent Line */}
      <section className="bg-white border-b py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">
            {stateDescription || `Looking for social work jobs in ${stateName}? This page helps you find roles by city, license type, and setting — including medical social work, hospice, school social work, and mental health positions.`}
          </p>
        </div>
      </section>

      {/* Popular Cities */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6 text-center">
            Popular {stateName} Job Markets
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {cities.map((city, index) => (
              <Link 
                key={index}
                to={`/jobs?location=${encodeURIComponent(city)}`}
                className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all text-sm"
              >
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-gray-700 hover:text-primary">{city}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by Specialty - The 6 Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Browse {stateName} Jobs by Setting
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find opportunities in hospitals, hospice agencies, schools, behavioral health facilities, and government organizations across {stateName}.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialtyCategories.map((category, index) => (
              <Link 
                key={index}
                to={category.href}
                className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <category.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                  {category.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {category.description}
                </p>
                <span className="inline-flex items-center text-sm text-primary font-medium">
                  View Jobs <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 mb-4">Not sure which setting fits you? Upload your resume and we'll match your experience to {stateName} openings.</p>
            <Link to="/register">
              <Button className="bg-secondary hover:bg-secondary-hover text-white">
                <FileText className="w-4 h-4 mr-2" />
                Upload Resume (AI Parse)
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Browse by License */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6 text-center">
            Search by License Level
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={`/jobs?license=LCSW&location=${encodeURIComponent(stateName)}`}>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white h-12 px-8">
                LCSW Jobs in {stateName}
              </Button>
            </Link>
            <Link to={`/jobs?license=LMSW&location=${encodeURIComponent(stateName)}`}>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white h-12 px-8">
                LMSW Jobs in {stateName}
              </Button>
            </Link>
            <Link to={`/jobs?license=LBSW&location=${encodeURIComponent(stateName)}`}>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white h-12 px-8">
                BSW Jobs in {stateName}
              </Button>
            </Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">
            {stateName} employers often list roles by license and experience level. Use filters to quickly find the best match.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-8 text-center">
            {stateName} Social Worker Jobs FAQ
          </h2>
          <div className="space-y-4">
            {finalFaqItems.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-start gap-3 mb-2">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <h3 className="font-semibold text-gray-900">{item.question}</h3>
                </div>
                <p className="text-gray-600 pl-8">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related States */}
      <section className="py-8 bg-gray-50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold text-gray-700 mb-4 text-center">Explore Other States</h3>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/texas-social-worker-jobs" className="text-primary hover:underline">Texas</Link>
            <span className="text-gray-300">•</span>
            <Link to="/california-social-worker-jobs" className="text-primary hover:underline">California</Link>
            <span className="text-gray-300">•</span>
            <Link to="/florida-social-worker-jobs" className="text-primary hover:underline">Florida</Link>
            <span className="text-gray-300">•</span>
            <Link to="/new-york-social-worker-jobs" className="text-primary hover:underline">New York</Link>
            <span className="text-gray-300">•</span>
            <Link to="/michigan-social-worker-jobs" className="text-primary hover:underline">Michigan</Link>
            <span className="text-gray-300">•</span>
            <Link to="/nevada-social-worker-jobs" className="text-primary hover:underline">Nevada</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Find Your Next Social Work Job in {stateName}?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Search openings, create your profile, and upload your resume for faster matching.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/jobs?location=${encodeURIComponent(stateName)}`}>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 h-12">
                Search {stateName} Jobs
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8 h-12">
                Create Job Seeker Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StateSocialWorkerJobsPage;
